import { useNavigate } from "react-router-dom";
import { useMeetingStore } from "../../store/meeting.store";
import { useWebexMeetingStore } from "../../store/webex.store";
import { useEffect, useRef, useState } from "react";
import { getStopReasons } from "../../api/reasonApis";
import { socket } from "../mediasoup/socket";
import { toast } from "react-hot-toast";
import Popover from "../Popover";
import {
  RecordingIcon,
  BroadcastIcon,
  BroadcastOffIcon,
  EndIcon,
  QuickFlagIcon,
  StartFlagIcon,
  StopFlagIcon,
} from "../Icons";
import logger from "../../utils/logger";
import { recordingSocket } from "../mediasoup/recordingSocket";
import { QuickFlagModal, StopFlagModal } from "./FlagModal";
import { useFlagTimer } from "../../hooks/useFlagTimer";
import { FLAG_TYPE_MAP } from "../../pages/protected/controller/helpers/types";
import "./icons.css";
import { submitFlag } from "../../api/controllerDashboardApis";
import type { FlagCategory } from "./flagConstants.";
import { CategoryChips } from "./CategoryChips";
import ToolbarButton from "./ToolbarButton";
const ensureCaseStarted = (cnr?: string) => {
  if (!cnr) {
    toast.error("Please start the case first");
    return false;
  }
  return true;
};

const ControlBar = () => {
  const navigate = useNavigate();
  const userData = useMeetingStore((s) => s.userData);
  const selectedCase = useMeetingStore((s) => s.selectedCase);
  const recording = useMeetingStore((s) => s.recording);
  const activeRecordingCnr = useMeetingStore((s) => s.activeRecordingCnr);
  const setIsStreaming = useMeetingStore((s) => s.setIsStreaming);
  const setRecording = useMeetingStore((s) => s.setRecording);
  const setActiveRecordingCnr = useMeetingStore((s) => s.setActiveRecordingCnr);
  const setRecordingStartedAt = useMeetingStore((s) => s.setRecordingStartedAt);
  const setStreamingStartedAt = useMeetingStore((s) => s.setStreamingStartedAt);
  const causes = useMeetingStore((s) => s.causes);
  const setCauses = useMeetingStore((s) => s.setCauses);
  const setSelectedCase = useMeetingStore((s) => s.setSelectedCase);
  const meeting = useWebexMeetingStore((s) => s.meeting);
  const cnr = useMeetingStore((s) => s.selectedCase?.cnr);
  const actionBy = useMeetingStore((s) => s.userData?._id);
  const courtRoomData = useMeetingStore((s) => s.courtRoomData);
  const stopLocalStreams = useMeetingStore((s) => s.stopLocalStreams);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [isStreamingPaused, setIsStreamingPaused] = useState(false);
  const [showStreamingReasons, setShowStreamingReasons] = useState(false);
  const streamingRef = useRef<HTMLDivElement>(null);

  const [_isRecordingStopped, setIsRecordingStopped] = useState(false);
  const [streamCat, setStreamCat] = useState<FlagCategory | null>(null);

  const [showRecordingReasons, setShowRecordingReasons] = useState(false);
  const [streamingReasons, setStreamingReasons] = useState<any[]>([]);
  const [recordingReasons, setRecordingReasons] = useState<any[]>([]);
  const recordingRef = useRef<HTMLDivElement>(null);

  const [selectedReasons, setSelectedReasons] = useState<string[]>([]);

  // Flag states
  const [showQuickFlag, setShowQuickFlag] = useState(false);
  const [showStopFlag, setShowStopFlag] = useState(false);
  const [quickFlagStart, setQuickFlagStart] = useState<Date>(new Date());
  const [quickFlagEnd, setQuickFlagEnd] = useState<Date>(new Date());

  const timer = useFlagTimer();

  logger.log("streaming reason..", streamingReasons);

  const prevCnrRef = useRef<string | undefined>(undefined);

  const noSession = !cnr;
  const isRecordingActive = recording === "start";
  const isStreamingActive = isStreamingPaused;

  const flagsAllowed = !noSession && (isRecordingActive || isStreamingActive);

  const validateFlagAllowed = () => {
    if (!ensureCaseStarted(cnr)) return false;

    if (!isRecordingActive && !isStreamingActive) {
      toast.error("Please start Recording or Streaming before creating a flag");
      return false;
    }

    return true;
  };

  const handleFlagSubmit = async (
    cat: FlagCategory,
    reason: string,
    start: Date,
    end: Date,
  ) => {
    try {
      if (!validateFlagAllowed()) return;
      const durSec = Math.round((end.getTime() - start.getTime()) / 1000);
      const mm = Math.floor(durSec / 60);
      const ss = String(durSec % 60).padStart(2, "0");

      const fmtTime = (d: Date) =>
        d.toLocaleTimeString("en-IN", {
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
          hour12: false,
        });

      const payload = {
        caseNumber: cnr,
        flagType: FLAG_TYPE_MAP[cat],
        flagSubType: reason,
        segmentDuration: `${mm}:${ss}`,
        startTime: start.getTime(),
        endTime: end.getTime(),
      };

      console.log("[FLAG] submitFlag payload →", payload);

      const created = await submitFlag(payload);
      recordingSocket.emit("flag-created", {
        caseNumber: cnr,
        flagId: created._id,
      });

      toast.success(
        `Flag submitted — ${FLAG_TYPE_MAP[cat]} · ${reason} (${fmtTime(start)}–${fmtTime(end)})`,
        { duration: 4000 },
      );
    } catch (err) {
      console.error("[FLAG] submitFlag error:", err);
      toast.error("Failed to submit flag");
    }

    setShowQuickFlag(false);
    setShowStopFlag(false);
    timer.reset();
  };

  useEffect(() => {
    if (prevCnrRef.current === cnr) return;
    prevCnrRef.current = cnr;

    // Reset streaming button to "Start Stream"
    setIsStreamingPaused(false);
    setIsStreaming(false);

    // Stop any running flag timer and close open flag modals
    timer.reset();
    setShowQuickFlag(false);
    setShowStopFlag(false);

    // Close any open reason popovers
    setShowStreamingReasons(false);
    setShowRecordingReasons(false);
    setSelectedReasons([]);
    setStreamCat(null);
  }, [cnr]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const fetchReasons = async () => {
      try {
        const res = await getStopReasons();

        if (res.data?.status && Array.isArray(res.data.data)) {
          const active = res.data.data.filter(
            (r: any) => r.status === "active",
          );
          const streaming = active.filter((r: any) => r.type === "streaming");
          const recording = active.filter((r: any) => r.type === "recording");

          setStreamingReasons(streaming);
          setRecordingReasons(recording);
        }
      } catch (err) {
        logger.error("Error fetching reasons:", err);
      }
    };
    fetchReasons();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        streamingRef.current &&
        !streamingRef.current.contains(event.target as Node)
      )
        setShowStreamingReasons(false);
      if (
        recordingRef.current &&
        !recordingRef.current.contains(event.target as Node)
      )
        setShowRecordingReasons(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const speak = (text: string) => {
    const voices = window.speechSynthesis.getVoices();
    const femaleVoice =
      voices.find(
        (v) =>
          v.name.toLowerCase().includes("female") ||
          v.name.toLowerCase().includes("woman") ||
          v.name.toLowerCase().includes("google uk english female") ||
          v.name.toLowerCase().includes("google en-in"),
      ) || voices[0];

    const utter = new SpeechSynthesisUtterance(text);
    utter.lang = "en-IN";
    utter.pitch = 1;
    utter.rate = 1;
    utter.voice = femaleVoice;
    window.speechSynthesis.speak(utter);
  };

  const toggleReason = (reason: string) => {
    setSelectedReasons((prev) =>
      prev.includes(reason)
        ? prev.filter((r) => r !== reason)
        : [...prev, reason],
    );
  };

  const handleStreamingAction = async (type: string) => {
    try {
      console.log("handle streaming action......", type);
      // socket?.emit("streaming-action", {
      //   cnr,
      //   roomId: courtRoomData.courtRoomId,
      //   status: type === "pause" ? "pause" : "resume",
      //   reasons: type === "pause" ? selectedReasons : [],
      //   userId: userData._id,
      // });

      // await updateStreamingStatus({
      //   cnr,
      //   actionBy,
      //   type,
      //   reasons: selectedReasons,
      // });

      if (!socket) {
        toast.error("Unable to connect to meeting socket");
        return;
      }

      const actionAllowed = await new Promise<boolean>((resolve) => {
        socket.emit(
          "streaming-action",
          {
            cnr,
            roomId: courtRoomData.courtRoomId,
            status: type === "pause" ? "pause" : "resume",
            reasons: type === "pause" && streamCat ? [streamCat] : [],
            userId: userData._id,
            mode: "rec+live-streaming",
          },
          (response: any) => {
            if (response?.status === false) {
              toast.error(response?.message || "Streaming is not available", {
                id: "streaming-not-available",
              });
              resolve(false);
              return;
            }

            resolve(true);
          },
        );
      });

      if (!actionAllowed) {
        return;
      }

      if (type == "pause") {
        console.log("pause......");
        recordingSocket?.emit("stream_pause", { sessionId: cnr });
      }
      if (type == "resume") {
        console.log("resume......");
        recordingSocket?.emit("stream_resume", { sessionId: cnr });
      }

      console.log("STREAM clicked  mode: rec+live-streaming", {
        cnr,
        status: type,
      });
      setShowStreamingReasons(false);
      setSelectedReasons([]);

      if (type === "resume") {
        setIsStreamingPaused(true);
        setIsStreaming(true);
        speak("The streaming of the session has started");
        toast.success("Streaming started");
      } else if (type === "pause") {
        setIsStreamingPaused(false);
        setIsStreaming(false);
        speak("The streaming of the session has been paused");
        toast.success("Streaming paused");
      }
    } catch (err) {
      logger.error("Error updating streaming status:", err);
      toast.error("Failed to update streaming status");
    }
  };

  const onLeaveCall = () => {
    if (socket) {
      setRecording("stop");
      stopLocalStreams();
      setIsStreaming(false);
      socket.emit("end-meeting", {
        roomId: courtRoomData.courtRoomId,
        roomNo: courtRoomData.courtRoomNo,
        actionBy,
        cnr,
      });
      socket.emit("recording-action", {
        cnr,
        roomId: courtRoomData.courtRoomId,
        status: "stop",
        reasons: selectedReasons,
        userId: userData._id,
        mode: "rec-only",
      });
      console.log("RECORD clicked  mode: rec-only", { cnr, status: "start" });
      meeting?.endMeetingForAll();
      setShowModal(false);
      navigate("/dashboard");
    }
  };

  useEffect(() => {
    const handleRercordingStart = (data: any) => {
      const belongsToCurrentRoom =
        !data?.roomId ||
        !courtRoomData?.courtRoomId ||
        String(data.roomId) === String(courtRoomData.courtRoomId);

      if (belongsToCurrentRoom && data.sessionId) {
        setIsRecordingStopped(true);
        setRecording("start");
        setActiveRecordingCnr(data.sessionId || cnr || null);
        setRecordingStartedAt(data.startedAt || Date.now());
        toast.success("Recording has started", { id: "start-recording" });
      }
    };
    socket?.on("recording_ack", (data: any) => {
      if (data.status === "start") {
        handleRercordingStart(data);
        speak("Recording has started");
      } else if (data.status === "stop") {
        if (
          data?.sessionId &&
          activeRecordingCnr &&
          data.sessionId !== activeRecordingCnr
        ) {
          return;
        }
        setIsRecordingStopped(false);
        setRecording("stop");
        setActiveRecordingCnr(null);
        setRecordingStartedAt(null);
        setIsStreamingPaused(false);
        setIsStreaming(false);
        setStreamingStartedAt(null);
        toast.success("Recording has been stopped");
        speak("Recording has been stopped");
      }
    });

    return () => {
      socket?.off("recording_ack");
    };
  }, [
    activeRecordingCnr,
    socket,
    cnr,
    courtRoomData?.courtRoomId,
    setActiveRecordingCnr,
    setRecording,
    setRecordingStartedAt,
    setIsStreaming,
    setStreamingStartedAt,
  ]);

  useEffect(() => {
    const handleStreamingUpdated = (data: any) => {
      if (
        data?.roomId &&
        courtRoomData?.courtRoomId &&
        String(data.roomId) !== String(courtRoomData.courtRoomId)
      ) {
        return;
      }
      const isLive = data?.status === "resume";
      setIsStreamingPaused(isLive);
      setIsStreaming(isLive);
      setStreamingStartedAt(isLive ? data?.startedAt || Date.now() : null);
    };

    const handleRecordingUpdated = (data: any) => {
      if (
        data?.roomId &&
        courtRoomData?.courtRoomId &&
        String(data.roomId) !== String(courtRoomData.courtRoomId)
      ) {
        return;
      }
      if (
        data?.status === "stop" &&
        data?.cnr &&
        activeRecordingCnr &&
        data.cnr !== activeRecordingCnr
      ) {
        return;
      }
      if (data?.status === "start") {
        setIsRecordingStopped(true);
        setRecording("start");
        setActiveRecordingCnr(data.cnr || cnr || null);
        setRecordingStartedAt(data.startedAt || Date.now());
        return;
      }
      if (data?.status === "stop") {
        setIsRecordingStopped(false);
        setRecording("stop");
        setActiveRecordingCnr(null);
        setRecordingStartedAt(null);
        setIsStreamingPaused(false);
        setIsStreaming(false);
        setStreamingStartedAt(null);
      }
    };

    socket?.on("streaming-action-updated", handleStreamingUpdated);
    socket?.on("recording-action-updated", handleRecordingUpdated);

    return () => {
      socket?.off("streaming-action-updated", handleStreamingUpdated);
      socket?.off("recording-action-updated", handleRecordingUpdated);
    };
  }, [
    activeRecordingCnr,
    cnr,
    courtRoomData?.courtRoomId,
    setActiveRecordingCnr,
    setIsStreaming,
    setRecordingStartedAt,
    setStreamingStartedAt,
  ]);

  useEffect(() => {
    if (socket && courtRoomData?.courtRoomId) {
      socket.emit(
        "get-actions",
        { roomId: courtRoomData.courtRoomId },
        (socketData: any) => {
          logger.log("socketData", socketData);
          if (!socketData?.status) return;

          if (socketData?.cnr) {
            const causeList = Array.isArray(causes) ? causes : [];
            const getSelectedCause = causeList.find(
              (c: any) => c.cnr === socketData.cnr,
            );
            let changed = false;
            const allCauses = causeList.map((c: any) => {
              if (c.cnr === socketData.cnr && c.hearingStatus !== "Active") {
                changed = true;
                return { ...c, hearingStatus: "Active" };
              }
              return c;
            });
            if (changed) {
              setCauses(allCauses);
            }
            if (getSelectedCause && selectedCase?.cnr !== socketData.cnr) {
              setSelectedCase({ ...getSelectedCause, hearingStatus: "Active" });
            }
          }
          const isRecording = !!socketData?.recording;
          const currentState = useMeetingStore.getState();
          const hasLocalRecordingStart =
            currentState.recording === "start" &&
            !!currentState.activeRecordingCnr &&
            (!socketData?.cnr ||
              currentState.activeRecordingCnr === socketData.cnr);

          if (isRecording || !hasLocalRecordingStart) {
            setRecording(isRecording ? "start" : "stop");
            setActiveRecordingCnr(isRecording ? socketData?.cnr || null : null);
            setRecordingStartedAt(
              isRecording ? socketData?.recordingStartedAt || Date.now() : null,
            );
          }

          // FIX: sync streaming button state with actual server state
          // (previously only recording was synced, streaming was ignored,
          // so the button always reset to "Start Stream" on reload/reconnect
          // even if streaming was already live on the server)
          const isLive = !!socketData?.streaming;
          setIsStreamingPaused(isLive);
          setIsStreaming(isLive);
          setStreamingStartedAt(
            isLive ? socketData?.streamingStartedAt || Date.now() : null,
          );
        },
      );
    }
    return () => {
      if (socket) {
        socket.off("get-actions");
      }
    };
  }, [
    socket,
    courtRoomData?.courtRoomId,
    causes,
    selectedCase?.cnr,
    setCauses,
    setActiveRecordingCnr,
    setIsStreaming,
    setRecording,
    setRecordingStartedAt,
    setSelectedCase,
    setStreamingStartedAt,
  ]);
  //   if (!selectedCase || !courtRoomData?.courtRoomId) {
  //     setRecording("stop");

  //     toast.error("No case selected, first select a case", {
  //       id: "no-case-selected",
  //     });

  //     return;
  //   }

  //   socket.emit("recording-action", {
  //     cnr,
  //     roomId: courtRoomData.courtRoomId,
  //     status: recording === "stop" ? "start" : "stop",
  //     userId: userData._id,
  //   });
  // };
  // logger.log("selectedCase:", selectedCase);
  const handleCoHostRecording = () => {
    if (!selectedCase || !courtRoomData?.courtRoomId) {
      setRecording("stop");

      toast.error("No case selected, first select a case", {
        id: "no-case-selected",
      });

      return;
    }

    if (recording === "start") {
      setShowRecordingReasons(true);
      return;
    }

    socket.emit("recording-action", {
      cnr,
      roomId: courtRoomData.courtRoomId,
      status: "start",
      userId: userData._id,
      mode: isStreamingActive ? "rec+live-streaming" : "rec-only",
    });
  };

  return (
    <div className="sticky bottom-0 w-full p-2  ctrl-bar">
      <div className="flex items-center justify-center gap-5 md:gap-7 ctrl-bar__inner">
        <div ref={streamingRef} className="relative flex flex-col items-center">
          <div className="group relative flex items-center">
            <ToolbarButton
              label={isStreamingActive ? "Stop Stream" : "Start Stream"}
              disabled={noSession || !isRecordingActive}
              onClick={() => {
                if (isStreamingActive) {
                  setShowStreamingReasons((prev) => !prev);
                } else {
                  handleStreamingAction("resume");
                }
              }}
              className={`${noSession || !isRecordingActive ? "opacity-40 cursor-not-allowed" : ""}
    ${isStreamingActive ? "bar-btn--stream-live" : ""}
  `}
            >
              {isStreamingActive ? (
                <BroadcastOffIcon className="w-5 h-5 text-white bar-btn__icon live-pulse" />
              ) : (
                <BroadcastIcon className="w-5 h-5 text-white/50 bar-btn__icon" />
              )}
            </ToolbarButton>
          </div>

          {showStreamingReasons && (
            <div
              className="popup-overlay show"
              style={{ position: "fixed" }}
              onClick={(e) => {
                if (e.target === e.currentTarget) {
                  setShowStreamingReasons(false);
                  setStreamCat(null);
                }
              }}
            >
              <div className="popup">
                <div className="popup-header">
                  <div className="popup-title">Reason for stopping stream</div>
                </div>

                <div className="section-label">Category</div>
                <CategoryChips
                  selected={streamCat}
                  onSelect={(c) => setStreamCat(c)}
                />

                <div className="popup-actions">
                  <button
                    type="button"
                    className="btn-cancel"
                    onClick={() => {
                      setShowStreamingReasons(false);
                      setStreamCat(null);
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn-submit"
                    disabled={streamCat === null}
                    onClick={() => {
                      handleStreamingAction("pause");
                      setStreamCat(null);
                    }}
                  >
                    Confirm
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* ── RECORDING ── */}
        <div
          ref={recordingRef}
          className="relative flex h-10 items-center justify-center"
        >
          <ToolbarButton
            label={isRecordingActive ? "Stop Recording" : "Start Recording"}
            disabled={noSession}
            onClick={handleCoHostRecording}
            className={`${noSession ? "opacity-40 cursor-not-allowed" : ""}
    ${isRecordingActive ? "bar-btn--rec-live" : ""}
  `}
          >
            <RecordingIcon
              className={`bar-btn__icon ${recording === "start" ? "text-red-500 fill-red-500 animate-pulse" : ""}`}
            />
          </ToolbarButton>

          {showRecordingReasons && (
            <div className="absolute bottom-full left-1/2 -translate-x-1/2  z-50">
              <Popover
                position="top"
                showProp={showRecordingReasons}
                trigger={<></>}
              >
                <div className="popover-reasons">
                  {recordingReasons.length > 0 && (
                    <h3 className="popover-reasons__title">
                      Reasons for stop recording
                    </h3>
                  )}
                  {recordingReasons.length === 0 ? (
                    <button
                      disabled={selectedReasons.length === 0}
                      onClick={() => {
                        socket.emit("recording-action", {
                          cnr,
                          roomId: courtRoomData.courtRoomId,
                          status: "stop",
                          reasons: selectedReasons,
                          userId: userData._id,
                        });
                        setShowRecordingReasons(false);
                        setIsStreamingPaused(false);
                        setIsStreaming(false);
                        setSelectedReasons([]);
                      }}
                      className={`popover-reasons__confirm ${selectedReasons.length === 0 ? "popover-reasons__confirm--disabled" : ""}`}
                    >
                      Confirm
                    </button>
                  ) : (
                    <>
                      {recordingReasons.map((r: any, i: number) => (
                        <label
                          key={r._id || i}
                          className="popover-reasons__item"
                        >
                          <input
                            type="checkbox"
                            checked={selectedReasons.includes(r.reason)}
                            onChange={() => toggleReason(r.reason)}
                          />
                          <span title={r.message}>{r.reason}</span>
                        </label>
                      ))}
                      <button
                        disabled={selectedReasons.length === 0}
                        onClick={() => {
                          socket.emit("recording-action", {
                            cnr,
                            roomId: courtRoomData.courtRoomId,
                            status: "stop",
                            reasons: selectedReasons,
                            userId: userData._id,
                          });
                          setShowRecordingReasons(false);
                          setSelectedReasons([]);
                          setIsStreamingPaused(false);
                          setIsStreaming(false);
                          toast.success("Recording has been stopped");
                          speak("Recording has been stopped");
                        }}
                        className={`popover-reasons__confirm ${selectedReasons.length === 0 ? "popover-reasons__confirm--disabled" : ""}`}
                      >
                        Confirm
                      </button>
                    </>
                  )}
                </div>
              </Popover>
            </div>
          )}
        </div>

        <div className="ctrl-bar__sep" />

        {/* ── QUICK FLAG ── */}
        <ToolbarButton
          label={timer.running ? "Stop Flag" : "Quick Flag"}
          disabled={!flagsAllowed || timer.running}
          onClick={() => {
            if (!validateFlagAllowed()) return;
            const now = new Date();
            setQuickFlagStart(new Date(now.getTime() - 5000));
            setQuickFlagEnd(new Date(now.getTime() + 5000));
            setShowQuickFlag(true);
          }}
          className={`bar-btn--flag-quick 
            ${!flagsAllowed || timer.running ? "opacity-40 cursor-not-allowed" : ""}`}
        >
          <QuickFlagIcon className="bar-btn__icon" />
        </ToolbarButton>

        {/* ── START / STOP FLAG ── */}
        <ToolbarButton
          label={timer.running ? "Stop Flag" : "Start Flag"}
          disabled={!flagsAllowed}
          onClick={() => {
            if (!validateFlagAllowed()) return;
            if (!timer.running) {
              timer.start();
            } else {
              const startTime = timer.stop(); // validate BEFORE stop so timer stays intact on fail
              if (startTime) {
                setQuickFlagStart(startTime);
                setQuickFlagEnd(new Date());
                setShowStopFlag(true);
              }
            }
          }}
          className={`bar-btn--flag-in
    ${!flagsAllowed ? "opacity-40 cursor-not-allowed" : ""}
    ${timer.running ? "bar-btn--flag-in--active" : ""}
  `}
        >
          {timer.running && (
            <span className="flag-timer-badge">
              {timer.format(timer.elapsed)}
            </span>
          )}
          {timer.running ? (
            <StopFlagIcon className="bar-btn__icon" />
          ) : (
            <StartFlagIcon className="bar-btn__icon" />
          )}
        </ToolbarButton>

        <div className="ctrl-bar__sep" />

        {/* ── END ── */}
        <ToolbarButton
          label="End Session"
          className=" bar-btn--end"
          onClick={() => setShowModal(true)}
        >
          <EndIcon className="bar-btn__icon" />
        </ToolbarButton>
      </div>

      {/* ── Modals ── */}
      {showQuickFlag && (
        <QuickFlagModal
          start={quickFlagStart}
          end={quickFlagEnd}
          onSubmit={handleFlagSubmit}
          onCancel={() => setShowQuickFlag(false)}
        />
      )}
      {showStopFlag && (
        <StopFlagModal
          rawStart={quickFlagStart}
          rawEnd={quickFlagEnd}
          onSubmit={handleFlagSubmit}
          onCancel={() => {
            setShowStopFlag(false);
            timer.reset();
          }}
        />
      )}

      {showModal && (
        <div
          style={{
            position: "fixed",
            bottom: "8%",
            left: "40%",
            zIndex: 999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "16px",
          }}
        >
          <div
            style={{
              background: "#1e1560",
              border: "0.5px solid rgba(255,255,255,0.15)",
              borderRadius: "16px",
              padding: "24px",
              width: "100%",
              maxWidth: "380px",
            }}
          >
            {/* header */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "10px",
                marginBottom: "12px",
              }}
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#f87171"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
              <span
                style={{ fontSize: "16px", fontWeight: 700, color: "#fff" }}
              >
                End Session?
              </span>
            </div>

            {/* body */}
            <p
              style={{
                fontSize: "13px",
                color: "rgba(255,255,255,0.6)",
                lineHeight: 1.6,
                marginBottom: "20px",
              }}
            >
              This will end the entire courtroom session and exit you from this
              screen.
            </p>

            {/* actions */}
            <div style={{ display: "flex", gap: "10px" }}>
              <button
                onClick={() => setShowModal(false)}
                style={{
                  flex: 1,
                  padding: "11px",
                  borderRadius: "10px",
                  background: "rgba(255,255,255,0.08)",
                  border: "0.5px solid rgba(255,255,255,0.15)",
                  color: "rgba(255,255,255,0.75)",
                  fontSize: "13px",
                  fontWeight: 600,
                  cursor: "pointer",
                  fontFamily: "inherit",
                }}
              >
                Cancel
              </button>
              <button
                onClick={onLeaveCall}
                style={{
                  flex: 2,
                  padding: "11px",
                  borderRadius: "10px",
                  background: "linear-gradient(135deg, #dc2626, #e11d48)",
                  border: "none",
                  color: "#fff",
                  fontSize: "13px",
                  fontWeight: 700,
                  cursor: "pointer",
                  fontFamily: "inherit",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "8px",
                }}
              >
                <svg
                  width="15"
                  height="15"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="12" cy="12" r="10" />
                  <line x1="8" y1="12" x2="16" y2="12" />
                </svg>
                Yes, End Session
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
export default ControlBar;
